import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, Panel, StatusBadge, BranchSel, Spinner } from "@/components/kit";
import { api, useApi, fmtErr, peso, label, today, dt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Row = ({ k, v, strong, tone }) => (
  <div className={`flex justify-between py-1 text-sm ${strong ? "font-bold" : ""}`}><span className="text-muted-foreground">{k}</span><span className={`font-mono ${tone || ""}`}>{v}</span></div>
);

function Card({ r, date, reload }) {
  const { can } = useAuth();
  const close = async () => {
    const notes = window.prompt("Reconciliation notes (optional)") ?? null;
    if (notes === null) return;
    try {
      const { data } = await api.post("/reconciliation/close", { branch_id: r.branch_id, date, notes });
      toast.success(`Day closed: ${label(data.status)}`);
      reload();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  return (
    <Panel testid={`recon-${r.branch_code}`} title={<span>{r.branch_code} — {r.branch_name}</span>}
      action={r.discrepancy ? <StatusBadge status="discrepancy" tone="red" /> : <StatusBadge status="balanced" />}>
      <div className="grid gap-6 md:grid-cols-3">
        <div>
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Operations</div>
          <Row k="Vehicle entries" v={r.entries} /><Row k="Vehicle exits" v={r.exits} /><Row k="Sessions created" v={r.sessions_created} />
          <Row k="Sessions closed" v={r.sessions_closed} /><Row k="Paid transactions" v={r.paid_transactions} /><Row k="Failed transactions" v={r.failed_transactions} />
          <Row k="Unpaid exits" v={r.unpaid_exits} tone={r.unpaid_exits ? "text-red-500" : ""} /><Row k="Manual overrides" v={`${r.manual_overrides} (${r.overrides_unpaid} unpaid)`} />
        </div>
        <div>
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Revenue</div>
          <Row k="Gross revenue" v={peso(r.gross_revenue)} /><Row k="Discounts" v={peso(r.discounts)} /><Row k="Refunds" v={peso(r.refunds)} tone="text-amber-500" />
          <Row k="Voids" v={peso(r.voids)} tone="text-amber-500" /><Row k="Net collections" v={peso(r.net_collections)} strong tone="text-emerald-500" />
          <Row k="Billed (closed sessions)" v={peso(r.billed_closed)} /><Row k="Collected (closed)" v={peso(r.collected_closed)} />
          <Row k="Variance" v={peso(r.variance)} tone={r.variance ? "text-red-500" : ""} /><Row k="Unpaid balances" v={peso(r.unpaid_balance)} tone={r.unpaid_balance ? "text-red-500" : ""} />
        </div>
        <div>
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Payment channels</div>
          {Object.entries(r.channels).map(([k, v]) => <Row key={k} k={label(k)} v={peso(v)} />)}
          {!Object.keys(r.channels).length && <div className="text-sm text-muted-foreground">No collections</div>}
        </div>
      </div>
      <div className="mt-4 space-y-1.5" data-testid={`recon-flags-${r.branch_code}`}>
        {r.flags.map((f, i) => <div key={i} className="flex items-center gap-2 rounded bg-red-500/10 px-3 py-1.5 text-xs text-red-500"><AlertTriangle className="h-3.5 w-3.5" />{f}</div>)}
        {!r.flags.length && <div className="flex items-center gap-2 text-xs text-emerald-500"><CheckCircle2 className="h-3.5 w-3.5" /> No discrepancies detected</div>}
      </div>
      <div className="mt-4 flex items-center justify-between border-t pt-3">
        {r.closed ? <span className="flex items-center gap-2 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5" />Closed by {r.closed.closed_by} • {dt(r.closed.closed_at)} • {label(r.closed.status)}</span> : <span className="text-xs text-muted-foreground">Not yet closed</span>}
        {can("approve") && <Button size="sm" data-testid={`recon-close-${r.branch_code}`} onClick={close}>{r.closed ? "Re-close day" : "Close day"}</Button>}
      </div>
    </Panel>
  );
}

export default function Reconciliation() {
  const { branch, setBranch } = useAuth();
  const [date, setDate] = useState(today());
  const { data, reload } = useApi("/reconciliation", { date, branch_id: branch });
  return (
    <div className="fade-up">
      <PageHeader title="Daily Reconciliation" subtitle="Entries vs exits vs sessions vs payments — discrepancies flagged automatically" testid="recon-header">
        <Input type="date" data-testid="recon-date" className="w-44" value={date} onChange={(e) => setDate(e.target.value)} />
        <BranchSel value={branch} onChange={setBranch} testid="recon-branch" />
      </PageHeader>
      {!data ? <Spinner /> : <div className="space-y-6">{data.rows.map((r) => <Card key={r.branch_id} r={r} date={date} reload={reload} />)}</div>}
    </div>
  );
}
